import dayjs from "dayjs";

import type { MetricValueResponseDto } from "../libs/cellera-sdk";

export interface PeriodCycle {
  startDate: string | Date;
  endDate: string | Date;
  diffDays: number;
  records: MetricValueResponseDto[];
}

export enum MenstruationFlowEnum {
  LIGHT = "light",
  MEDIUM = "medium",
  HEAVY = "heavy",
  NONE = "none",
  NOT_APPLICABLE = "notApplicable",
  UNSPECIFIED = "unspecified",
}

// Allow a single missed or delayed sync day while still treating the records
// as one continuous period cycle.
const PERIOD_CYCLE_MAX_GAP_DAYS = 2;

/**
 * Group consecutive menstruation records into period cycles
 * and return the latest one.
 *
 * @param records - filtered records with valueText = light/medium/heavy
 * @returns latest PeriodCycle or null
 */
export const getLatestPeriodCycle = (
  records: MetricValueResponseDto[],
): PeriodCycle | null => {
  if (!records || records.length === 0) return null;

  const sorted = [...records].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
  );

  const cycles: PeriodCycle[] = [];
  let currentCycle: MetricValueResponseDto[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prevDate = dayjs(sorted[i - 1].startDate);
    const currDate = dayjs(sorted[i].startDate);
    const gapDays = currDate.diff(prevDate, "day");

    if (gapDays <= PERIOD_CYCLE_MAX_GAP_DAYS) {
      currentCycle.push(sorted[i]);
    } else {
      cycles.push(buildCycle(currentCycle));
      currentCycle = [sorted[i]];
    }
  }

  cycles.push(buildCycle(currentCycle));

  return cycles[cycles.length - 1];
};

const buildCycle = (records: MetricValueResponseDto[]): PeriodCycle => {
  const startDate = records[0].startDate;
  const endDate = records[records.length - 1].startDate;
  const diffDays = dayjs(endDate).diff(dayjs(startDate), "day") + 1;

  return { startDate, endDate, diffDays, records };
};
